import React, { useCallback, useEffect, useState } from "react";

import type { ServerDataSuccess } from "./route";

import { useFunctionsGatsby } from "~/hooks/useFunctionsGatsby";
import { useReviewContext } from "~/hooks/useReviewSnippet";
import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

import { ReviewNugget as ReviewNuggetUI } from "@bared/ui";

export interface ReviewNuggetProps {
  variantSkus: ServerDataSuccess["variantSkus"];
  onReviewsClick?: () => void;
}

type ReviewNugget = {
  id: string;
  author: string;
  rating: number;
  text: string;
};

type ReviewNuggetResponse = {
  success: boolean;
  reviewNugget: ReviewNugget;
};

export function ReviewNugget({
  variantSkus,
  onReviewsClick: onReviewsClickProp,
}: ReviewNuggetProps) {
  const { productReviewNugget } = useFunctionsGatsby();
  const { totalReviewsCount } = useReviewContext();
  const googleTagManager = useGoogleTagManager();

  const [reviewNugget, setReviewNugget] = useState<ReviewNugget | null>(null);

  const convertSkusToQueryString = (skus: string[]): string => {
    return skus.map((sku) => `sku%5B%5D=${encodeURIComponent(sku)}`).join("&");
  };

  const getReviewNugget = useCallback(async () => {
    const skuQueryString = convertSkusToQueryString(variantSkus);
    const { status, body } = await productReviewNugget(skuQueryString);

    if (status !== "success") {
      return;
    }
    const { success, reviewNugget: reviewNuggetData } =
      body as ReviewNuggetResponse;

    if (!success) {
      return;
    }

    setReviewNugget(reviewNuggetData);
  }, [variantSkus]);

  const onReviewsClick = useCallback(() => {
    // Use custom handler if provided
    if (onReviewsClickProp) {
      onReviewsClickProp();
    }

    googleTagManager.push({
      event: "Button Clicked",
      properties: {
        label: `Read # Reviews`,
      },
    });
  }, [onReviewsClickProp, googleTagManager]);

  useEffect(() => {
    getReviewNugget();
  }, [getReviewNugget]);

  return (
    <>
      {!!reviewNugget && (
        <ReviewNuggetUI
          author={reviewNugget.author}
          rating={reviewNugget.rating}
          text={reviewNugget.text}
          reviewsCount={totalReviewsCount?.toString() || ""}
          onReviewsClick={onReviewsClick}
        />
      )}
    </>
  );
}
